import logo from "../../assets/images/logo.svg";
import { useState } from "react";
import { MdVisibility } from "react-icons/md";
import { MdVisibilityOff } from "react-icons/md";

import { Card, Input, Typography } from "@material-tailwind/react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { emailOnlyForm, emailValidation } from "./authValidations";
import { yupResolver } from "@hookform/resolvers/yup";
import api from "../../services/apiService";
import ApiRoutes from "../../services/apiRoutes";
import { useMutation } from "react-query";
import { handleErrorMessage } from "../../utility";
import LoadingButton from "../../components/common/buttons/LoadingButton";
import ErrorParagraph from "../../components/common/ErrorParagraph";
import AuthService, {
  LoginVerificationErrors,
} from "../../services/authService";
import { routerPaths } from "../../services/path";
import { useOnboardingDetailsStore } from "../../store/onboarding/Onboarding";

function SignIn() {
  const [passwordShown, setPasswordShown] = useState(false);
  const navigation = useNavigate();
  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<emailOnlyForm>({
    mode: "onChange",
    resolver: yupResolver(emailValidation),
  });

  const { setDetails } = useOnboardingDetailsStore();

  const loginMutation = useMutation(
    (data: emailOnlyForm) => api.post(ApiRoutes.login, data),
    {
      onSuccess: (data) => {
        console.log(data);
        const verificationError = AuthService.getLoginVerificationError(
          data.data.payload.statusMessage
        );
        reset();
        clearErrors();
        if (verificationError === LoginVerificationErrors.UnverifiedEmail) {
          setDetails({
            email: data.data.payload.data.email,
            phoneNumber: data.data.payload.data.phoneNumber,
          });

          navigation(routerPaths.verifyEmail);
          return;
        }

        if (
          verificationError === LoginVerificationErrors.UnverifiedPhoneNumber
        ) {
          setDetails({
            email: data.data.payload.data.email,
            phoneNumber: data.data.payload.data.phoneNumber,
          });
          navigation(routerPaths.verifyPhoneNumber);
          return;
        }

        AuthService.saveUser(data.data.payload);
        navigation(routerPaths.dashboard);
        window.location.reload();
      },
      onError: (error) => {
        if (error === LoginVerificationErrors.UnverifiedEmail) {
          navigation(routerPaths.verifyEmail);
          return;
        }

        if (error === LoginVerificationErrors.UnverifiedPhoneNumber) {
          navigation(routerPaths.verifyPhoneNumber);
          return;
        }
        handleErrorMessage(
          error,
          "Unable to login user. Please try again later."
        );
      },
    }
  );
  const { isLoading } = loginMutation;

  const onSubmit = (data: emailOnlyForm) => {
    AuthService.logOut();
    loginMutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="lg:flex w-screen md:h-screen bg-gray-50">
        <div className="invisible lg:visible bg-map-background bg-no-repeat w-3/6 relative flex flex-col justify-center items-center">
          <img
            className="  absolute md:top-10 md:left-20"
            src={logo}
            alt="GIGM Logo"
          />
          <div className="text-4xl">
            <h1>It’s easier to get</h1>
            <h2 className="inline">started with{` `}</h2>
            <h2 className="inline font-extrabold text-primary">GIGM Boat</h2>
          </div>
        </div>
        <img className="ml-20 md:hidden" src={logo} alt="GIGM Logo" />
        <div className="flex items-center justify-center ">
          <div className="md:w-[60%] md:m-24 m-10">
            <h2 className=" text-2xl text-center">Sign into your account</h2>
            <Card placeholder={undefined} color="transparent" shadow={false}>
              <div className="mt-8 mb-2 w-80 max-w-screen-lg sm:w-96 my-auto ">
                <div className="mb-1 flex flex-col gap-6">
                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-3"
                  >
                    Email Or PhoneNumber
                  </Typography>
                  <Input
                    crossOrigin={undefined}
                    size="lg"
                    placeholder="name@mail.com"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    {...register("emailOrPhoneNumber")}
                  />
                  {errors.emailOrPhoneNumber && (
                    <ErrorParagraph
                      message={errors.emailOrPhoneNumber.message!}
                    />
                  )}
                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-3"
                  >
                    Password
                  </Typography>
                  <Input
                    type={passwordShown ? "text" : "password"}
                    size="lg"
                    placeholder="********"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    icon={
                      passwordShown ? (
                        <MdVisibility onClick={() => setPasswordShown(false)} />
                      ) : (
                        <MdVisibilityOff
                          onClick={() => setPasswordShown(true)}
                        />
                      )
                    }
                    crossOrigin={undefined}
                    {...register("password")}
                  />
                  {errors.password && (
                    <ErrorParagraph message={errors.password.message!} />
                  )}
                </div>

                <Link to={routerPaths.forgotPassword}>
                  <Typography placeholder={undefined} className="text-right">
                    Forgot Password
                  </Typography>
                </Link>

                <LoadingButton isLoading={isLoading}>GET STARTED</LoadingButton>
                <Typography
                  placeholder={undefined}
                  color="gray"
                  className="mt-4 text-center font-normal"
                >
                  Don’t have an account?{" "}
                  <a
                    href={routerPaths.selectAccount}
                    className="font-medium text-gray-900"
                  >
                    Sign up
                  </a>
                </Typography>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </form>
  );
}

export default SignIn;
